import React, { useCallback, useMemo, useState } from 'react';
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';
import { TextInput, HelperText } from 'react-native-paper';
import DateTimePicker, {
  DateTimePickerAndroid,
} from '@react-native-community/datetimepicker';
import { Button as PaperButton } from 'react-native-paper';
import moment from 'moment';
import { useTheme, GTFonts } from '../../theme';
import { fs, s, v } from '../../utils/libs';

export type DisplayFormat =
  | 'default'
  | 'iso'
  | 'monthYear'
  | 'yearOnly'
  | string;

export type DatePickerFieldProps = {
  value: Date | string | null;
  onChange: (date: Date | null) => void;
  onDismiss?: () => void;
  label?: string;
  placeholder?: string;
  displayFormat?: DisplayFormat;
  variant?: 'outlined' | 'compact' | 'pickerOnly';
  optional?: boolean;
  minimumDate?: Date;
  maximumDate?: Date;
  mode?: 'date' | 'time' | 'datetime';
  disabled?: boolean;
  style?: ViewStyle;
  inputStyle?: ViewStyle;
  error?: string;
};

const resolveFormat = (displayFormat: DisplayFormat): string => {
  if (displayFormat === 'default') return 'll';
  if (displayFormat === 'iso') return 'YYYY-MM-DD';
  if (displayFormat === 'monthYear') return 'MMM YYYY';
  if (displayFormat === 'yearOnly') return 'YYYY';
  return displayFormat || 'll';
};

const parseValueToDate = (value: Date | string | null): Date | null => {
  if (value == null || value === '') return null;
  if (value instanceof Date)
    return Number.isFinite(value.getTime()) ? value : null;
  const str = String(value).trim();
  if (!str) return null;
  const parsed = moment(str, ['YYYY-MM-DD', moment.ISO_8601], true);
  return parsed.isValid() ? parsed.toDate() : null;
};

const DatePickerField: React.FC<DatePickerFieldProps> = ({
  value,
  onChange,
  onDismiss,
  label,
  placeholder = 'Select date',
  displayFormat = 'default',
  variant = 'outlined',
  optional = false,
  minimumDate,
  maximumDate,
  mode = 'date',
  disabled = false,
  style,
  inputStyle,
  error,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const [showPicker, setShowPicker] = useState(false);

  const dateValue = useMemo(() => parseValueToDate(value), [value]);
  const displayDate = dateValue ?? new Date();
  const pickerMode = mode === 'datetime' ? 'date' : mode;
  const formatStr = resolveFormat(displayFormat);
  const displayText = dateValue ? moment(dateValue).format(formatStr) : '';

  const webPlaceholderTheme = useMemo(() => {
    if (Platform.OS === 'web' && theme.colors.textSecondary) {
      try {
        const hex = theme.colors.textSecondary.replace('#', '');
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        const placeholderColor = `rgba(${r}, ${g}, ${b}, 0.55)`;
        return {
          theme: {
            fonts: {
              regular: {
                fontFamily: GTFonts.regular,
                fontWeight: '400' as any,
              },
            },
            colors: {
              placeholder: placeholderColor,
              onSurfaceVariant: placeholderColor,
            },
          },
        };
      } catch {
        return {};
      }
    }
    return {};
  }, [theme.colors.textSecondary]);

  const handlePickerChange = useCallback(
    (event: any, selected?: Date) => {
      if (event?.type === 'dismissed' || !selected) {
        if (Platform.OS === 'ios') setShowPicker(false);
        onDismiss?.();
        return;
      }
      if (Platform.OS === 'ios') setShowPicker(false);
      onChange(selected);
    },
    [onChange, onDismiss],
  );

  const openDatePicker = useCallback(() => {
    if (disabled) return;
    if (Platform.OS === 'android') {
      DateTimePickerAndroid.open({
        value: displayDate,
        mode: pickerMode,
        display: 'calendar',
        minimumDate,
        maximumDate,
        onChange: (event: any, selected?: Date) => {
          if (event?.type === 'set' && selected) {
            onChange(selected);
          }
        },
      });
    } else {
      setShowPicker(prev => !prev);
    }
  }, [disabled, displayDate, pickerMode, minimumDate, maximumDate, onChange]);

  if (variant === 'pickerOnly') {
    return (
      <DateTimePicker
        value={displayDate}
        mode={mode}
        display={Platform.OS === 'ios' ? 'spinner' : 'default'}
        minimumDate={minimumDate}
        maximumDate={maximumDate}
        onChange={handlePickerChange}
      />
    );
  }

  const isWeb = Platform.OS === 'web';

  const commonPickerProps = {
    value: displayDate,
    mode: pickerMode,
    onChange: handlePickerChange,
    minimumDate,
    maximumDate,
  };

  const webDateInputStyle: any = isWeb
    ? {
        width: '100%',
        padding: `${s(8)}px ${s(12)}px`,
        fontSize: fs(14),
        borderRadius: theme.borderRadius.md,
        border: `1px solid ${theme.colors.border}`,
        backgroundColor: theme.colors.surface,
        color: theme.colors.text,
        boxSizing: 'border-box',
        height: v(40),
        ...(inputStyle as any),
      }
    : undefined;

  const outlinedRadius =
    (inputStyle as any)?.borderRadius != null
      ? (inputStyle as any).borderRadius
      : theme.borderRadius.md;
  const outlineColor = theme.colors.borderGray || theme.colors.border;
  const WebDateTimePicker = DateTimePicker as any;

  const fieldContent = (
    <>
      {isWeb ? (
        <WebDateTimePicker
          {...commonPickerProps}
          style={[styles.webPicker, style]}
          inputStyle={webDateInputStyle}
        />
      ) : (
        <>
          {variant === 'outlined' ? (
            <TouchableOpacity
              onPress={openDatePicker}
              disabled={disabled}
              activeOpacity={0.8}>
              <TextInput
                mode="outlined"
                value={displayText}
                editable={false}
                placeholder={placeholder}
                right={
                  <TextInput.Icon
                    icon="calendar"
                    onPress={openDatePicker}
                    forceTextInputFocus={false}
                  />
                }
                onPress={openDatePicker}
                {...webPlaceholderTheme}
                style={[styles.input, inputStyle]}
                outlineStyle={{ borderRadius: outlinedRadius }}
                outlineColor={outlineColor}
                activeOutlineColor={theme.colors.primary}
              />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              activeOpacity={0.8}
              onPress={openDatePicker}
              disabled={disabled}
              style={[styles.inputBox, styles.inputCompact, inputStyle]}>
              <Text style={styles.inputText} numberOfLines={1}>
                {displayText || placeholder}
              </Text>
            </TouchableOpacity>
          )}

          {Platform.OS === 'ios' && showPicker && (
            <View style={styles.iosPickerContainer}>
              <Text style={styles.modalTitle}>{label || 'Pick date'}</Text>
              <DateTimePicker
                {...commonPickerProps}
                display="spinner"
                style={styles.iosPicker}
              />
              <View style={styles.modalActions}>
                <PaperButton onPress={() => setShowPicker(false)}>
                  Done
                </PaperButton>
              </View>
            </View>
          )}
        </>
      )}
    </>
  );

  return (
    <View style={[styles.container, style]}>
      {label ? <Text style={styles.label}>{label}</Text> : null}
      {fieldContent}
      {error ? (
        <HelperText type="error" visible={!!error}>
          {error}
        </HelperText>
      ) : null}
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      marginBottom: v(6),
    },
    label: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
      marginBottom: v(4),
    },
    input: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    webPicker: {
      alignSelf: 'stretch',
    },
    inputBox: {
      borderWidth: 1,
      borderColor: theme.colors.borderGray || theme.colors.border,
      borderRadius: theme.borderRadius.md,
      paddingVertical: s(8),
      paddingHorizontal: s(12),
      backgroundColor: theme.colors.surface,
    },
    inputCompact: {
      paddingVertical: s(6),
      minHeight: v(36),
      justifyContent: 'center',
    },
    inputText: {
      color: theme.colors.text,
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(18),
    },
    iosPickerContainer: {
      marginTop: v(8),
    },
    modalTitle: {
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      fontSize: fs(16),
      marginBottom: v(8),
    },
    iosPicker: {
      alignSelf: 'stretch',
      height: v(120),
    },
    modalActions: {
      marginTop: v(12),
      alignItems: 'flex-end',
    },
  });

export default DatePickerField;
